import { lazy, Suspense, type ComponentType } from "react";
import { createBrowserRouter, Navigate, Outlet, type RouteObject } from "react-router";
import { useSession } from "@/api/hooks";
import type { SessionStage } from "@/api/types/SessionStage";
import { RouteError } from "@/components/RouteError";
import { LoadError, PageSpinner } from "@/components/ui";
import { AppShell } from "@/layouts/AppShell";
import { FocusShell } from "@/layouts/FocusShell";
import { LoginPage } from "@/pages/login/LoginPage";
import { VerifyPage } from "@/pages/login/VerifyPage";
import { SetupPage } from "@/pages/login/SetupPage";
import { ProjectsPage } from "@/pages/ProjectsPage";
import { ProjectLayout, RequireScrum } from "@/pages/project/ProjectLayout";
import { ProjectOverview } from "@/pages/project/ProjectOverview";
import { ProjectMembers } from "@/pages/project/ProjectMembers";
import { ProjectSettings } from "@/pages/project/ProjectSettings";
import { UsersPage } from "@/pages/admin/UsersPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { NotFoundPage } from "@/pages/NotFoundPage";

/** Code-split a named page export; shows a spinner while its chunk loads. */
function page(load: () => Promise<Record<string, unknown>>, name: string) {
  const Lazy = lazy(() => load().then((m) => ({ default: m[name] as ComponentType })));
  return (
    <Suspense fallback={<PageSpinner />}>
      <Lazy />
    </Suspense>
  );
}

/** Where each session stage belongs. */
export function homeFor(stage: SessionStage | null | undefined): string {
  switch (stage) {
    case "active":
      return "/projects";
    case "pending_totp":
      return "/login/verify";
    case "pending_setup":
      return "/login/setup";
    default:
      return "/login";
  }
}

/** Renders children only in the given session stage; otherwise redirects to where the user belongs. */
function RequireStage({ stage }: { stage: SessionStage | null }) {
  const session = useSession();
  if (session.isPending) return <PageSpinner />;
  if (session.isError) return <LoadError onRetry={() => void session.refetch()} />;
  const current = session.data.stage;
  if (current !== stage) return <Navigate to={homeFor(current)} replace />;
  return <Outlet />;
}

function RequirePlatformManager() {
  const session = useSession();
  if (session.data?.user?.role !== "manager") return <NotFoundPage />;
  return <Outlet />;
}

const routes: RouteObject[] = [
  { path: "/", element: <Navigate to="/projects" replace /> },
  {
    element: <RequireStage stage={null} />,
    children: [{ path: "/login", element: <LoginPage /> }],
  },
  {
    element: <RequireStage stage="pending_totp" />,
    children: [{ path: "/login/verify", element: <VerifyPage /> }],
  },
  // The setup page guards itself: after confirming, the session is already active
  // but the page must keep showing the recovery codes.
  { path: "/login/setup", element: <SetupPage /> },
  {
    element: <RequireStage stage="active" />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: "/projects", element: <ProjectsPage /> },
          { path: "/inbox", element: page(() => import("@/pages/InboxPage"), "InboxPage") },
          { path: "/testing", element: page(() => import("@/pages/testing/TestingBoardPage"), "TestingBoardPage") },
          { path: "/tasks", element: page(() => import("@/pages/WorkPage"), "WorkPage") },
          { path: "/tasks/:scope", element: page(() => import("@/pages/WorkPage"), "WorkPage") },
          { path: "/notifications", element: page(() => import("@/pages/NotificationsPage"), "NotificationsPage") },
          { path: "/notifications/:id", element: page(() => import("@/pages/NotificationPage"), "NotificationPage") },
          { path: "/analytics", element: page(() => import("@/pages/analytics/OrgAnalyticsPage"), "OrgAnalyticsPage") },
          {
            path: "/p/:key",
            element: <ProjectLayout />,
            children: [
              { index: true, element: page(() => import("@/pages/tasks/TasksPage"), "TasksPage") },
              { path: "tasks", element: <Navigate to=".." relative="path" replace /> },
              { path: "tasks/:taskKey", element: page(() => import("@/pages/tasks/TaskPage"), "TaskPage") },
              { path: "bugs", element: page(() => import("@/pages/bugs/BugsPage"), "BugsPage") },
              { path: "bugs/new", element: page(() => import("@/pages/bugs/NewBugPage"), "NewBugPage") },
              { path: "bugs/:n", element: page(() => import("@/pages/bugs/BugPage"), "BugPage") },
              { path: "learnings", element: page(() => import("@/pages/bugs/LearningsPage"), "LearningsPage") },
              {
                element: <RequireScrum />,
                children: [
                  { path: "backlog", element: page(() => import("@/pages/sprints/BacklogPage"), "BacklogPage") },
                  { path: "sprints", element: page(() => import("@/pages/sprints/SprintsPage"), "SprintsPage") },
                  { path: "sprints/:n", element: page(() => import("@/pages/sprints/SprintPage"), "SprintPage") },
                  { path: "poker", element: page(() => import("@/pages/sprints/PokerPage"), "PokerPage") },
                  { path: "sprints/:n/report", element: page(() => import("@/pages/sprints/SprintReportPage"), "SprintReportPage") },
                  { path: "standup", element: page(() => import("@/pages/standup/StandupPage"), "StandupPage") },
                ],
              },
              { path: "flow", element: page(() => import("@/pages/flow/FlowPage"), "FlowPage") },
              { path: "testing", element: page(() => import("@/pages/testing/TestingBoardPage"), "TestingBoardPage") },
              { path: "epics", element: page(() => import("@/pages/epics/EpicsPage"), "EpicsPage") },
              { path: "epics/:n", element: page(() => import("@/pages/epics/EpicPage"), "EpicPage") },
              { path: "releases", element: page(() => import("@/pages/releases/ReleasesPage"), "ReleasesPage") },
              { path: "releases/:id", element: page(() => import("@/pages/releases/ReleasePage"), "ReleasePage") },
              { path: "analytics", element: page(() => import("@/pages/analytics/ProjectAnalyticsPage"), "ProjectAnalyticsPage") },
              { path: "overview", element: <ProjectOverview /> },
              { path: "members", element: <ProjectMembers /> },
              { path: "settings", element: <ProjectSettings /> },
              { path: "settings/:section", element: <ProjectSettings /> },
            ],
          },
          {
            element: <RequirePlatformManager />,
            children: [
              { path: "/admin/users", element: <UsersPage /> },
              { path: "/admin/tags", element: page(() => import("@/pages/admin/TagsPage"), "TagsPage") },
              { path: "/admin/gitlab", element: page(() => import("@/pages/admin/GitlabPage"), "GitlabPage") },
            ],
          },
          { path: "/profile", element: <ProfilePage /> },
          { path: "/profile/:section", element: <ProfilePage /> },
          { path: "*", element: <NotFoundPage /> },
        ],
      },
      {
        // The test runner is full-screen: no sidebar.
        element: <FocusShell />,
        children: [
          { path: "/p/:key/tasks/:taskKey/run/:n", element: page(() => import("@/pages/tasks/RunnerPage"), "RunnerPage") },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter([{ errorElement: <RouteError />, children: routes }]);
